import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { useSessionSetupFlow } from "../hooks/useSessionSetupFlows";
import HeroCardLayout from "./layout/HeroCardLayout";
import MicrophonePermissioningContainer from "./entryflow/MicrophonePermissioningContainer";
import LoadingSessionComponent from "./entryflow/LoadingSession";
import { getSessionSpec } from "../utils/sessionEndpoints";
import { useNavUtils } from "../utils/navUtils";
import { 
  setSelectorId,
  setResLinkParamsId,
  selectResLinkParamsId,
  setOrganizationId,
  selectOrganizationId,
} from '../store/experienceSlice';
import {
  setUser,
  setActivePixelStreamingExperience,
  selectActivePixelStreamingExperience,
} from "../store/userSlice";
import { getPartnerUserAndExperience } from "./entryflow/userFlowUtils";
import LoadingSessionErrorComponent from "./entryflow/LoadingSessionError";
import { NativeBridge } from "../utils/parentAppBridge";

type PartnerEntryState =
  | "initial"
  | "error"
  | "success"
  | "microphonepermissioning"
  | "sessioncreating"
  | "sessionloadingerror";

const PartnerEntry: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { goNav } = useNavUtils();
  const { handleManualSessionSetup } = useSessionSetupFlow();

  const activePixelStreamingExperience = useSelector(selectActivePixelStreamingExperience);
  const organizationId = useSelector(selectOrganizationId); 
  const resLinkParamsId = useSelector(selectResLinkParamsId);

  const [currentView, setCurrentView] = useState<PartnerEntryState>("initial");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  const launchSession = useCallback(async () => {
    NativeBridge.emitToNative({ type: "sessionLaunching" });

    console.log("[PartnerEntry] launchSession, activePixelStreamingExperience orgId:", organizationId, " resLinkParamsId:", resLinkParamsId);
    const sessionSpec = getSessionSpec(organizationId || "", undefined, undefined, resLinkParamsId);
    setCurrentView("sessioncreating");
    try {
      const sessionId = await handleManualSessionSetup(sessionSpec);
      NativeBridge.emitToNative({ type: "sessionLaunchSucceeded", payload: { sessionId } });
      goNav("/avatarstreamer");
    } catch (err) {
      let msg: string = "";
      if (err instanceof Error) {
        msg = err.message || "Failed to create session.";
      } else {
        msg = "Unknown error creating session.";
      }
      setStatusMessage(msg);
      NativeBridge.emitToNative({ type: "sessionError", payload: { errorType: "launchFailed", message: msg } });
      setCurrentView("sessionloadingerror");
    }
  }, [goNav, handleManualSessionSetup, activePixelStreamingExperience, organizationId, resLinkParamsId]);

  useEffect(() => {
    async function fetchAndSetUser() {
      const params = new URLSearchParams(location.search);
      const paramResLinkParamsId = params.get("res_link_params_id");
      const paramOrganizationId = params.get("org_id");

      if (!paramResLinkParamsId || !paramOrganizationId) {
        const msg = "Invalid link. Please check the link and try again.";
        setStatusMessage(msg);
        setCurrentView("error");
        NativeBridge.emitToNative({ type: "sessionError", payload: { errorType: "invalidConfiguration", message: msg } });
        return;
      }

      try {
        const { user, experience } = await getPartnerUserAndExperience(paramResLinkParamsId, paramOrganizationId);

        if (experience.organization_id) {
          dispatch(setOrganizationId(experience.organization_id))
        } else {
          dispatch(setOrganizationId(paramOrganizationId));
        }
        dispatch(setResLinkParamsId(paramResLinkParamsId));
        dispatch(setActivePixelStreamingExperience(experience));
        dispatch(setSelectorId(undefined));
        dispatch(setUser(user));
        setCurrentView("microphonepermissioning");
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error("[PartnerEntry] Error fetching user:", msg);
        NativeBridge.emitToNative({ type: "sessionError", payload: { errorType: "invalidUser", msg } });
        setStatusMessage("Failed to fetch user. Please try again.");
        setCurrentView("error");
      }
    }

    NativeBridge.emitToNative({ type: "sessionFlowStarted" });
    fetchAndSetUser();
  }, [location.search, dispatch]);

  if (currentView === "error") {
    return (
      <LoadingSessionErrorComponent errorMessage={statusMessage} onRetry={launchSession} /> 
    );
  }

  if (currentView === "success") {
    return (
      <LoadingSessionErrorComponent errorMessage={"It worked!"} /> 
    );
  }

  if (currentView === "sessioncreating") {
    return <LoadingSessionComponent />;
  }

  if (currentView === "sessionloadingerror") {
    return <LoadingSessionErrorComponent errorMessage={statusMessage} onRetry={launchSession} />;
  }

  if (currentView === "microphonepermissioning") {
    return (
      <HeroCardLayout>
        <MicrophonePermissioningContainer 
          onMicrophonePermissioned={launchSession} 
          botName={activePixelStreamingExperience?.bot_name} />
      </HeroCardLayout>
    );
  }

  return (
      <HeroCardLayout showCard={false}>{null}</HeroCardLayout>
  )
}

export default PartnerEntry;